import React, { useEffect, useState } from "react";
import { Application, getApplication } from "@/api/applications";
import { Customer, getCustomer } from "@/api/customers";
import {
  getUsersByApplicationAndCustomerId,
  getAllActiveUsers,
  User,
} from "@/api/users";
import AddButton from "@/ui-controls/AddButton";
import NewAppCustomerUser from "@/components/users/app-customer-users/NewAppCustomerUserPanel";
import AppCustomerUserDetailList from "@/components/users/app-customer-users/AppCustomerUserDetailList";
import SearchBox from "@/ui-controls/SearchBox";
import { useNavigate } from "react-router-dom";
import { IconButton, MessageBar, MessageBarType } from "@fluentui/react";
import { useErrorNotification } from "@/utils/useErrorNotification";

interface Props {
  ApplicationId: number;
  CustomerId: number;
  ShowOnlyNewAppCustomerUserPanel: boolean;
  onCloseNewAppCustomerUserPanel?: () => any;
  onSaveAppCustomerUsers?: (users: User[]) => any;
  onCustomerLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
  backPath?: string;
}

const AppCustomerUsers = (props: Props) => {
  const navigate = useNavigate();
  const { errorMessage, showError, clearError } = useErrorNotification();
  const [loadingAllUsers, setLoadingAllUsers] = useState<boolean>(false);
  const [loadingAppCustomerUsers, setLoadingAppCustomerUsers] =
    useState<boolean>(false);
  const [newAppCustomerUserPanelOpenned, setNewAppCustomerUserPanelOpenned] =
    useState<boolean>(props.ShowOnlyNewAppCustomerUserPanel);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [appCustomerUsers, setAppCustomerUsers] = useState<User[]>([]);
  const [nonAppCustomerUsers, setNonAppCustomerUsers] = useState<User[]>([]);
  const [application, setApplication] = useState<Application | undefined>(
    undefined
  );
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isSearchPerformed, setIsSearchPerformed] = useState<boolean>(false);

  useEffect(() => {
    loadAllUsers();
    if (props.ApplicationId) {
      getApplicationById();
    }
    if (props.CustomerId) {
      getCustomerById();
    }
  }, [props.ApplicationId, props.CustomerId]);

  useEffect(() => {
    if (props.ApplicationId && props.CustomerId) {
      loadAppCustomerUsers();
    }
  }, [props.ApplicationId, props.CustomerId]);

  useEffect(() => {
    const users = allUsers.filter(
      (u) => !appCustomerUsers.find((cu) => cu.Id === u.Id)
    );

    setNonAppCustomerUsers(users);

    handleUserSearch("");
  }, [allUsers, appCustomerUsers]);

  const loadAllUsers = () => {
    setLoadingAllUsers(true);
    getAllActiveUsers()
      .then((result) => {
        setAllUsers(result);
        setLoadingAllUsers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAllUsers(false);
      });
  };

  const loadAppCustomerUsers = () => {
    setLoadingAppCustomerUsers(true);
    getUsersByApplicationAndCustomerId(props.ApplicationId, props.CustomerId)
      .then((result) => {
        setAppCustomerUsers(result);
        setLoadingAppCustomerUsers(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingAppCustomerUsers(false);
      });
  };

  const getApplicationById = () => {
    getApplication(props.ApplicationId)
      .then((result) => {
        setApplication(result);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const getCustomerById = () => {
    getCustomer(props.CustomerId)
      .then((result) => {
        setCustomer(result);
        if (props.onCustomerLoaded && result?.Name) {
          props.onCustomerLoaded(result.Name);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    showError(error);
  };

  const handleUserSearch = (searchValue: string) => {
    const hasSearchValue = searchValue.trim().length > 0;
    setIsSearchPerformed(hasSearchValue);

    if (hasSearchValue) {
      const filteredUsers = appCustomerUsers.filter((user) =>
        user.EmailAddress.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredUsers(filteredUsers);
    } else {
      setFilteredUsers([]);
    }
  };

  const handleRefresh = () => {
    setIsSearchPerformed(false);
    setFilteredUsers([]);
    loadAppCustomerUsers();
  };

  return (
    <>
      {!props.ShowOnlyNewAppCustomerUserPanel ? (
        <div className="page-header">
          <div className="breadcrumb-left-section">
            {props.backPath && (
              <IconButton
                iconProps={{ iconName: "Back" }}
                title="Back to App Customers"
                onClick={() => navigate(props.backPath!)}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: { fontSize: 18, color: "#0078d4" },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                }}
              />
            )}
            <div>
              {props.breadcrumbTitle && (
                <h2 className="page-breadcrumb-title">
                  {props.breadcrumbTitle}
                </h2>
              )}
              {props.breadcrumbSubtitle && (
                <div style={{ color: "#605e5c", fontSize: "14px" }}>
                  {props.breadcrumbSubtitle}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AddButton
              text="User"
              onClick={() => setNewAppCustomerUserPanelOpenned(true)}
            />
            <SearchBox placeholder="Search" onSearch={handleUserSearch} />
            <IconButton
              iconProps={{ iconName: "Refresh" }}
              title="Refresh"
              onClick={handleRefresh}
              styles={{
                root: { width: 40, height: 40 },
                rootHovered: { backgroundColor: "#f3f2f1" },
                icon: { fontSize: 18, color: "#0078d4" },
                rootPressed: { backgroundColor: "#e1dfdd" },
              }}
            />
          </div>
        </div>
      ) : null}
      {!props.ShowOnlyNewAppCustomerUserPanel ? (
        <div>
          <AppCustomerUserDetailList
            UserList={
              isSearchPerformed
                ? filteredUsers.length === 0
                  ? []
                  : filteredUsers
                : appCustomerUsers
            }
            AppId={props.ApplicationId}
            CustomerId={props.CustomerId}
            onRemoveUsers={() => {
              loadAppCustomerUsers();
            }}
          />
        </div>
      ) : null}
      <NewAppCustomerUser
        application={application}
        customer={customer}
        nonCustomerUsers={nonAppCustomerUsers}
        allUsers={allUsers}
        isPanelOpenned={newAppCustomerUserPanelOpenned}
        onPanelClosed={() => {
          setNewAppCustomerUserPanelOpenned(false);
          if (props.onCloseNewAppCustomerUserPanel)
            props.onCloseNewAppCustomerUserPanel();
        }}
        onSaveNewUser={() => {
          loadAppCustomerUsers();
        }}
        onSave={() => {
          loadAppCustomerUsers();
        }}
      />
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={clearError}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 1000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default AppCustomerUsers;
